import { apiClient } from './client';

export interface ManufacturingDashboardMetrics {
  boms: {
    total: number;
    pendingApproval: number;
  };
  workCenters: {
    active: number;
  };
  inventoryPolicies: {
    total: number;
    belowSafetyStock: number;
  };
  plannedOrders: {
    pending: number;
  };
  sopCycles: {
    active: number;
    currentCycle: string | null;
    currentStatus: string | null;
  };
  suppliers: {
    active: number;
    avgLeadTimeDays: number;
  };
  npi: {
    inDevelopment: number;
    preLaunch: number;
  };
  promotions: {
    active: number;
    upcoming: number;
  };
  fiscalCalendar: {
    type: string | null;
  };
}

export type GLAccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE' | 'CONTRA_ASSET';
export type NormalBalance = 'DEBIT' | 'CREDIT';
export type JournalEntryStatus = 'POSTED' | 'REVERSED';

export interface ManufacturingGlAccount {
  id: string;
  accountNumber: string;
  name: string;
  accountType: GLAccountType;
  normalBalance: NormalBalance;
  description?: string | null;
  isActive: boolean;
  isSystem: boolean;
  parentId?: string | null;
  children?: ManufacturingGlAccount[];
  createdAt: string;
  updatedAt: string;
}

export interface ManufacturingJournalEntryLine {
  id: string;
  glAccountId: string;
  debitAmount: number | string;
  creditAmount: number | string;
  description?: string | null;
  glAccount?: Pick<ManufacturingGlAccount, 'id' | 'accountNumber' | 'name' | 'accountType'>;
}

export interface ManufacturingJournalEntry {
  id: string;
  entryDate: string;
  referenceType?: string | null;
  referenceId?: string | null;
  description?: string | null;
  status: JournalEntryStatus;
  currency?: string | null;
  createdAt: string;
  lines: ManufacturingJournalEntryLine[];
}

export interface ManufacturingJournalEntriesResponse {
  entries: ManufacturingJournalEntry[];
  total: number;
}

export interface ManufacturingGlAccountFilters {
  accountType?: GLAccountType;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  filters?: string;
}

export interface ManufacturingGlAccountsResponse {
  items: ManufacturingGlAccount[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ManufacturingJournalEntryFilters {
  status?: JournalEntryStatus;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  filters?: string;
}

export const manufacturingService = {
  async getDashboard(): Promise<ManufacturingDashboardMetrics> {
    const { data } = await apiClient.get<ManufacturingDashboardMetrics>('/manufacturing/dashboard');
    return data;
  },

  async getGLAccounts(filters?: ManufacturingGlAccountFilters): Promise<ManufacturingGlAccountsResponse> {
    const { data } = await apiClient.get<ManufacturingGlAccountsResponse>('/manufacturing/gl-accounts', {
      params: filters,
    });
    return data;
  },

  async getJournalEntries(filters?: ManufacturingJournalEntryFilters): Promise<ManufacturingJournalEntriesResponse> {
    const { data } = await apiClient.get<ManufacturingJournalEntriesResponse>('/manufacturing/journal-entries', {
      params: filters,
    });
    return data;
  },
};
